import { withoutDraftSource } from './shared-responder-v2-core-draft-source.helpers';
import { resolvePickedFollowUpPrompt } from './shared-responder-v2-core-follow-up-prompt.helpers';
import { selectIsDistributionAvailable } from './shared-responder-v2-core-session-answer.selectors';
import { makeResponderAnswerTurn } from './shared-responder-v2-core-session-turns.helpers';
import { RESPONDER_PHASES } from './shared-responder-v2-core-session.consts';
import {
  hasInFlightThread,
  selectCurrentThread,
} from './shared-responder-v2-core-session.selectors';
import type { ResponderSessionState } from './shared-responder-v2-core-session.types';
import { getThreadBehaviour } from './shared-responder-v2-core-thread-registry';
import {
  THREAD_STATUSES,
  TURN_INTENTS,
  TURN_ROLES,
  type ThreadKind,
} from './shared-responder-v2-core-thread.consts';
import type {
  ResponderAnswer,
  ResponderThread,
  ResponderTurn,
  ThreadEditRollback,
  TurnEdit,
} from './shared-responder-v2-core-thread.types';

function isAnsweredOrSkipped(thread: ResponderThread): boolean {
  return (
    thread.answer !== null ||
    thread.answerMessageId !== null ||
    thread.status === THREAD_STATUSES.Closed ||
    thread.turns.some((turn) => turn.role === TURN_ROLES.Responder)
  );
}

/**
 * Only the step settled last — answered, or left with a skip — may still have
 * its answer replaced. `editQuestionMessage` refuses once a later question is
 * answered, and although it accepts an edit across a skip, it discards that
 * skip with everything after the edited answer while the local session still
 * holds the skipped step as done: the next question's send is then refused.
 */
export function selectLatestSettledStepIndex(
  state: ResponderSessionState
): number | null {
  for (let index = state.steps.length - 1; index >= 0; index -= 1) {
    const step = state.steps[index];
    const thread =
      step === undefined ? undefined : state.threads[step.questionId];

    if (thread !== undefined && isAnsweredOrSkipped(thread)) {
      return index;
    }
  }

  return null;
}

function isLatestEditableStep(
  state: ResponderSessionState,
  stepIndex: number
): boolean {
  const step = state.steps[stepIndex];

  return (
    step !== undefined &&
    state.editing.mode !== 'editing' &&
    !hasInFlightThread(state) &&
    stepIndex === selectLatestSettledStepIndex(state) &&
    !selectIsDistributionAvailable(state, step.questionId)
  );
}

/**
 * A responder may edit the question on screen, or go back to the previous one
 * only if they have not yet answered the current one. Deliberately NOT gated
 * on `hasBranching` — editing a question with branching logic is allowed by
 * design, so a future `hasBranching` guard added here would be a regression,
 * not a fix.
 *
 * A poll whose live tally the responder has already seen is the one exception:
 * re-answering after seeing how everyone else voted would let the tally itself
 * change the answer, so that question is closed once its results are available.
 *
 * A question answered in the composer never opens this edit either: its answer
 * is rewritten in its own bubble (`selectEditableTurnIds`), which an open edit
 * would close and whose conversation it would take off screen.
 */
export function canEditStep(
  state: ResponderSessionState,
  stepIndex: number
): boolean {
  if (
    stepIndex > state.currentStepIndex ||
    !isLatestEditableStep(state, stepIndex)
  ) {
    return false;
  }

  const targetThread = state.threads[state.steps[stepIndex]?.questionId ?? ''];

  if (
    targetThread === undefined ||
    targetThread.answerMessageId === null ||
    !getThreadBehaviour(targetThread.kind).isAnswerEditable ||
    getThreadBehaviour(targetThread.kind).answersInComposer ||
    selectCurrentThread(state) === null
  ) {
    return false;
  }

  return (
    stepIndex < state.currentStepIndex ||
    targetThread.status !== THREAD_STATUSES.Failed
  );
}

export function selectCanEditCurrentAnswer(
  state: ResponderSessionState
): boolean {
  return (
    selectCurrentThread(state) !== null &&
    canEditStep(state, state.currentStepIndex)
  );
}

export function canEditPrevious(state: ResponderSessionState): boolean {
  return canEditStep(state, state.currentStepIndex - 1);
}

/**
 * Taken once per edit — a re-submit inside the same edit keeps the *original*
 * snapshot rather than the previous attempt's, so two refused tries still land
 * back on the answer the backend actually holds.
 */
function captureEditRollback(
  thread: ResponderThread,
  draft: ResponderAnswer | null
): ThreadEditRollback {
  return (
    thread.editRollback ?? {
      status: thread.status,
      answer: thread.answer,
      draft,
      turns: thread.turns,
      followUpCount: thread.followUpCount,
      answerMessageId: thread.answerMessageId,
      isFollowUpSkipped: thread.isFollowUpSkipped,
    }
  );
}

/**
 * Re-records the thread for an edit submission rather than appending to it: the
 * previous AI follow-up and its answer are dropped, and `requestSeq` is bumped
 * so an already-in-flight reply for the old submission arrives stale and is
 * discarded by the reducer.
 *
 * The discarded history is kept in `editRollback` rather than thrown away,
 * because the backend may refuse the edit outright — see `revertRejectedEdit`.
 * Its draft is the pick from before the edit began, not the changed one being
 * sent, so a refusal shows what the backend still holds.
 */
export function applyEditSubmission(
  thread: ResponderThread,
  edit: {
    readonly answer: ResponderAnswer;
    readonly draftBeforeEdit: ResponderAnswer | null;
  }
): ResponderThread {
  const { answer } = edit;
  const answerTurn = makeResponderAnswerTurn(
    thread.questionId,
    answer,
    TURN_INTENTS.PrimaryAnswer,
    0
  );
  const submitted: ResponderThread = {
    ...withoutDraftSource(thread),
    answer,
    editRollback: captureEditRollback(thread, edit.draftBeforeEdit),
    answerMessageId: null,
    followUpDraft: null,
    followUpCount: 0,
    isFollowUpSkipped: false,
    status: THREAD_STATUSES.AwaitingAssistant,
    requestSeq: thread.requestSeq + 1,
  };

  // The backend discards the old answer and everything after it, so no
  // earlier bubble may survive — a resumed pick arrives as one.
  return answerTurn === null
    ? { ...submitted, turns: [] }
    : { ...submitted, draft: null, turns: [answerTurn] };
}

/**
 * Puts a refused edit back the way the backend still has it.
 *
 * `Failed` is deliberately NOT the outcome: the backend refused the *change*,
 * not the answer, so the question is still answered and the rail's tick still
 * earned. Leaving it failed bricked the question — Continue demands `Closed`,
 * and the retry re-posted the same rejected message forever.
 *
 * `requestSeq` keeps its bumped value so the abandoned attempt's late reply is
 * still discarded as stale, and `followUpDraft` is cleared because the reply
 * slot belonged to a follow-up the edit had already dropped.
 */
export function revertRejectedEdit(thread: ResponderThread): ResponderThread {
  const { editRollback } = thread;

  if (editRollback === null) {
    return thread;
  }

  return {
    ...thread,
    ...editRollback,
    editRollback: null,
    followUpDraft: null,
    error: null,
  };
}

const NO_TURN_IDS: ReadonlySet<string> = new Set();

function isRewritableReply(
  kind: ThreadKind,
  askedFollowUp: ResponderTurn | null
): boolean {
  return askedFollowUp === null
    ? getThreadBehaviour(kind).answersInComposer
    : resolvePickedFollowUpPrompt(kind, askedFollowUp) === null;
}

/**
 * Only the responder's latest bubble may be rewritten — an earlier one already
 * drew the follow-up the responder answered after it. A pick is changed on its
 * question surface instead, so only typed answers and prose replies qualify.
 */
export function selectEditableTurnIds(
  state: ResponderSessionState
): ReadonlySet<string> {
  const thread = selectCurrentThread(state);

  if (
    state.phase !== RESPONDER_PHASES.Question ||
    thread === null ||
    thread.status === THREAD_STATUSES.Failed ||
    !isLatestEditableStep(state, state.currentStepIndex)
  ) {
    return NO_TURN_IDS;
  }

  let latestReply: ResponderTurn | null = null;
  let isLatestReplyRewritable = false;
  let askedFollowUp: ResponderTurn | null = null;

  for (const turn of thread.turns) {
    if (turn.intent === TURN_INTENTS.FollowUpQuestion) {
      askedFollowUp = turn;
    } else if (turn.role === TURN_ROLES.Responder) {
      latestReply = turn;
      isLatestReplyRewritable = isRewritableReply(thread.kind, askedFollowUp);
    }
  }

  return latestReply !== null && isLatestReplyRewritable
    ? new Set([latestReply.id])
    : NO_TURN_IDS;
}

/**
 * The thread as it reads while an edit is open but not yet sent: what the
 * edit will discard — the follow-ups drawn from the old answer, and any reply
 * to them — is taken off screen up front, and comes back if the edit is
 * cancelled. A pick edit keeps only the primary answer, as
 * `applyEditSubmission` does; a bubble being rewritten (`rewrittenTurnId`)
 * keeps itself and what came before it, as `applyTurnEdit` does. Once a pick
 * edit is submitted (`editRollback` held) the thread already reads that way.
 */
export function selectThreadDuringEdit(
  state: ResponderSessionState,
  thread: ResponderThread,
  rewrittenTurnId: string | null
): ResponderThread {
  const { editing } = state;

  if (
    editing.mode === 'editing' &&
    editing.questionId === thread.questionId &&
    thread.editRollback === null
  ) {
    return {
      ...thread,
      turns: thread.turns.filter(
        (turn) => turn.intent === TURN_INTENTS.PrimaryAnswer
      ),
    };
  }

  const rewrittenIndex =
    rewrittenTurnId === null
      ? -1
      : thread.turns.findIndex((turn) => turn.id === rewrittenTurnId);

  return rewrittenIndex === -1
    ? thread
    : { ...thread, turns: thread.turns.slice(0, rewrittenIndex + 1) };
}

export function canEditTurn(
  state: ResponderSessionState,
  turnId: string
): boolean {
  return selectEditableTurnIds(state).has(turnId);
}

/**
 * The edited bubble and everything after it are dropped, as the backend
 * discards the same messages; the pre-edit thread is kept in `editRollback` in
 * case it refuses the edit.
 */
export function applyTurnEdit(
  thread: ResponderThread,
  { turnId, text }: TurnEdit
): ResponderThread {
  const index = thread.turns.findIndex((turn) => turn.id === turnId);
  const edited = thread.turns[index];

  if (edited === undefined) {
    return thread;
  }

  const kept = thread.turns.slice(0, index);
  const answer: ResponderAnswer = { kind: 'text', text, transcript: null };
  const answerTurn = makeResponderAnswerTurn(
    thread.questionId,
    answer,
    edited.intent,
    index
  );
  const followUpCount = kept.filter(
    (turn) => turn.intent === TURN_INTENTS.FollowUpQuestion
  ).length;
  const isPrimaryAnswer = followUpCount === 0;

  return {
    ...withoutDraftSource(thread),
    draft: isPrimaryAnswer ? null : thread.draft,
    answerMessageId: isPrimaryAnswer ? null : thread.answerMessageId,
    answer,
    editRollback: captureEditRollback(thread, thread.draft),
    followUpDraft: null,
    turns: answerTurn === null ? kept : [...kept, answerTurn],
    followUpCount,
    isFollowUpSkipped: false,
    status: THREAD_STATUSES.AwaitingAssistant,
    requestSeq: thread.requestSeq + 1,
  };
}
